import useEmblaCarousel from 'embla-carousel-react';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { FaGoogle } from 'react-icons/fa';
import { useContent } from '@/lib/content';
import { Reveal, SectionHeading } from '@/components/Reveal';
import { scrollToId } from '@/lib/scroll';
import { trackEvent } from '@/lib/analytics';

export const Testimonials = () => {
  const { testimonials } = useContent();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start' });

  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 sm:py-32 bg-surface border-y border-white/5">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div>
            <SectionHeading chapter="08" label="Kind Words" title={<>Loved by families <span className="italic text-champagne">across Hyderabad</span></>} />
            <Reveal delay={0.15} className="mt-6 flex items-center gap-3" y={20}>
              <FaGoogle size={20} className="text-cream" />
              <span className="flex text-champagne">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
              </span>
              <span className="text-sm text-white/60 font-light" data-testid="google-rating">5.0 rating on Google — 100+ reviews</span>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="flex gap-3" y={20}>
            <button data-testid="testimonial-prev" onClick={() => emblaApi?.scrollPrev()} className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 hover:border-champagne hover:text-champagne transition-colors" aria-label="Previous testimonial">
              <ChevronLeft size={18} />
            </button>
            <button data-testid="testimonial-next" onClick={() => emblaApi?.scrollNext()} className="w-12 h-12 border border-white/20 flex items-center justify-center text-white/60 hover:border-champagne hover:text-champagne transition-colors" aria-label="Next testimonial">
              <ChevronRight size={18} />
            </button>
          </Reveal>
        </div>

        <Reveal className="mt-14">
          <div className="overflow-hidden" ref={emblaRef} data-testid="testimonial-carousel">
            <div className="flex">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.name} className="flex-[0_0_100%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] pr-4">
                  <div className="h-full bg-ink border border-white/10 p-8 flex flex-col hover:border-champagne/40 transition-colors duration-500">
                    <span className="flex text-champagne mb-6">
                      {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
                    </span>
                    <p className="font-serif text-xl font-light text-cream/90 leading-relaxed italic flex-1 line-clamp-[8]">&ldquo;{t.review}&rdquo;</p>
                    <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-4">
                      <span className="w-11 h-11 rounded-full bg-champagne/15 border border-champagne/40 flex items-center justify-center font-serif text-champagne text-sm">
                        {t.initials}
                      </span>
                      <div>
                        <p className="text-cream text-sm font-medium tracking-wide">{t.name}</p>
                        <p className="text-[11px] uppercase tracking-[0.25em] text-white/40 mt-0.5">{t.event}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal className="mt-16 text-center" delay={0.1}>
          <p className="font-serif text-2xl sm:text-3xl font-light text-cream">Planning Your Next Event?</p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-4">
            <button
              data-testid="testimonial-enquire-btn"
              onClick={() => { trackEvent('enquire_click', { location: 'testimonials' }); scrollToId('#enquiry'); }}
              className="btn-gold"
            >
              Let&rsquo;s Make It Unforgettable
            </button>
            <a data-testid="testimonial-read-all" href="/testimonials" className="btn-outline">Read All Reviews</a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
